import { Pagination } from "services/search";

import {
  FleetVehicleDriver,
  FleetVehicleDriverEditFormValues,
  FleetVehicleDriverRow,
  FleetVehicleDriverSearchFormValues,
} from "./types";

import { DataWithPagination } from "services/api/types";

import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import {
  transformFleetVehicleDriver,
  transformFleetVehicleDriverOnCreate,
  transformFleetVehicleDriverOnUpdate,
  transformFleetVehicleDriverRow,
} from "./transformations";
import { transformErrorResponse } from "services/api/transformation";

/*---------------------------------------------------------------------------------------
  SEARCH
-----------------------------------------------------------------------------------------*/
/**
 * Vozači tenanta. Bez `vehicleId` vraća sve — to koristi zajednička pretraga vozača;
 * s njim popis vozača jednog vozila. Ostali filtri dolaze kroz `filters`.
 */
export const getFleetVehicleDriversAPI = async (
  pagination: Pagination,
  vehicleId?: string,
  filters?: Partial<FleetVehicleDriverSearchFormValues>
): Promise<DataWithPagination<FleetVehicleDriverRow>> => {
  const params: string[] = [];

  const vehicle = vehicleId ?? filters?.vehicleId;

  if (vehicle) params.push(`vehicleId=${vehicle}`);
  if (filters?.search) params.push(`name=${encodeURIComponent(filters.search)}`);
  if (filters?.source) params.push(`source=${filters.source}`);
  if (filters?.expiryFrom)
    params.push(`expiryFrom=${filters.expiryFrom.toISOString()}`);
  if (filters?.expiryTo) params.push(`expiryTo=${filters.expiryTo.toISOString()}`);

  const path = `/v1/fleet/drivers${params.length ? `?${params.join("&")}` : ""}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleDriverRow
  );
};

/*---------------------------------------------------------------------------------------
  INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleDriverAPI = async (
  id: string
): Promise<FleetVehicleDriver> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/drivers/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleDriver(response.data);
};

/*---------------------------------------------------------------------------------------
  CREATE
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleDriverAPI = async (
  vehicleId: string,
  formValues: FleetVehicleDriverEditFormValues
): Promise<FleetVehicleDriver> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/drivers/",
      method: "POST",
      body: transformFleetVehicleDriverOnCreate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () => "Vozač je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleDriver(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleDriverAPI = async (
  vehicleId: string,
  id: string,
  formValues: FleetVehicleDriverEditFormValues
): Promise<FleetVehicleDriver> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/drivers/${id}`,
      method: "PUT",
      body: transformFleetVehicleDriverOnUpdate(formValues, vehicleId),
    },
    {
      getMessage: {
        success: () => "Vozač je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleDriver(response.data);
};

/*---------------------------------------------------------------------------------------
    DELETE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleDriverAPI = async (
  id: string,
  t: (key: string) => string
): Promise<FleetVehicleDriver> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/drivers/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.driver.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleDriver(response.data);
};